import { useState } from 'react'
import { adminClient, type AdminUserSummary } from '@/auth/adminClient'
import { Modal, inputStyle, labelStyle, btnPrimary, btnGhost, btnDanger } from './modalShared'

export default function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: AdminUserSummary
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const [displayName, setDisplayName] = useState(user.display_name)
  const [isActive, setIsActive] = useState(user.is_active)
  const [newPassword, setNewPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    setSubmitting(true)
    setError(null)
    try {
      const patch: { display_name?: string; is_active?: boolean; password?: string } = {}
      if (displayName !== user.display_name) patch.display_name = displayName
      if (isActive !== user.is_active) patch.is_active = isActive
      if (newPassword.length >= 8) patch.password = newPassword
      await adminClient.updateUser(user.id, patch)
      await onSaved()
    } catch (e) {
      setError(`Save failed (${String((e as Error).message ?? e)}).`)
    } finally {
      setSubmitting(false)
    }
  }

  async function deactivate() {
    if (!confirm(`Deactivate ${user.email}? They will no longer be able to sign in.`)) return
    setSubmitting(true)
    setError(null)
    try {
      await adminClient.deleteUser(user.id)
      await onSaved()
    } catch (e) {
      setError(`Deactivation failed (${String((e as Error).message ?? e)}).`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={`Edit ${user.email}`}
      onClose={onClose}
      footer={
        <>
          <button style={btnDanger} onClick={() => void deactivate()} disabled={submitting}>Delete</button>
          <div style={{ flex: 1 }} />
          <button style={btnGhost} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={() => void save()} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <label style={labelStyle}>Display name</label>
      <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={inputStyle} />

      <label style={{ ...labelStyle, marginTop: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        Active
      </label>

      <label style={labelStyle}>Reset password (leave blank to keep current)</label>
      <input
        type="text"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        placeholder="Min 8 characters"
        autoComplete="new-password"
        style={inputStyle}
      />
      <div style={{ fontSize: 11, color: 'var(--t-3)', marginTop: 6 }}>
        Resetting the password revokes all of this user’s active sessions.
      </div>

      {error && <div style={{ marginTop: 12, fontSize: 12, color: 'var(--crit)' }}>{error}</div>}
    </Modal>
  )
}
